<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import Layout from '@/router/layouts/main'
import { Helper, debounce } from '@/mixin/helper'
import { mapGetters } from 'vuex'
import WorkOrderPlanForm from './components/WorkOrderPlanForm'
import {
  getWorkOrderPlans,
  createWorkOrderPlan,
  updateWorkOrderPlan,
  deleteWorkOrderPlans,
} from './services/workOrderPlans'

export default {
  page: {
    title: 'WO Board',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    WorkOrderPlanForm,
  },
  mixins: [Helper],
  data() {
    return {
      filter: {
        search: '',
        status: '',
      },
      currentPage: 1,
      perPage: 10,
      pageOptions: [10],
      statusOptions: [
        'Preparing',
        'To Schedule',
        'Scheduled',
        'Confirmed Technician',
        'Install',
        'Completed',
      ],
      fields: [
        { key: 'wo_number', label: 'WO Number', sortable: true },
        { key: 'customer', label: 'Customer', sortable: true },
        { key: 'city', label: 'City', sortable: true },
        { key: 'technician_qty', label: 'Technician Qty', sortable: true },
        { key: 'plan_datetime', label: 'Plan Date', sortable: true },
        { key: 'estimated_time', label: 'Estimated Time', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'is_plan_confirmed', label: 'Confirmed', sortable: true },
        { key: 'actions', label: 'Actions' },
      ],
      workOrders: [],
      isLoading: false,
      isSubmitting: false,
      formMode: 'create',
      selectedWorkOrder: null,
      showFormModal: false,
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    title() {
      return this.$t('planner.column.woBoard')
    },
    description() {
      return this.$t('planner.subColumn.woBoard')
    },
    statusFilters() {
      return [
        { value: '', text: 'All' },
        ...this.statusOptions.map((status) => ({ value: status, text: status })),
      ]
    },
    totalRows() {
      return this.workOrders.length
    },
    paginationStart() {
      if (!this.totalRows) return 0

      return (this.currentPage - 1) * this.perPage + 1
    },
    paginationEnd() {
      return Math.min(this.currentPage * this.perPage, this.totalRows)
    },
    currentUserId() {
      return this.getUserId() || ''
    },
  },
  watch: {
    'filter.search': debounce(function () {
      this.currentPage = 1
      this.fetchWorkOrderPlans()
    }, 500),
    'filter.status'() {
      this.currentPage = 1
      this.fetchWorkOrderPlans()
    },
  },
  created() {
    this.fetchWorkOrderPlans()
  },
  methods: {
    normalizeResponseData(response) {
      const payload = response.data
      const data = payload && payload.data
      let items = []

      if (Array.isArray(payload)) items = payload
      else if (Array.isArray(data)) items = data
      else if (data && Array.isArray(data.paginated_result)) items = data.paginated_result
      else if (data && Array.isArray(data.data)) items = data.data
      else if (Array.isArray(payload.items)) items = payload.items

      return items.map((item) => ({
        ...item,
        id: item.id || item.work_order_plan_id,
      }))
    },
    getErrorMessage(error, fallback) {
      if (error.response && error.response.data) {
        return error.response.data.error || error.response.data.message || fallback
      }

      return error.message || fallback
    },
    getWorkOrderPlanParams() {
      const params = {
        page: this.currentPage,
        limit: this.perPage,
      }
      const search = this.filter.search.trim()

      if (search) params.search = search
      if (this.filter.status) params.filters = `status eq '${this.filter.status}'`

      return params
    },
    async fetchWorkOrderPlans() {
      this.isLoading = true
      try {
        const response = await getWorkOrderPlans(this.getWorkOrderPlanParams())
        this.workOrders = this.normalizeResponseData(response)
      } catch (error) {
        this.$toast.error(this.getErrorMessage(error, 'Failed loading WO plans'))
      } finally {
        this.isLoading = false
      }
    },

    resetFilter() {
      this.filter = {
        search: '',
        status: '',
      }
    },
    getStatusVariant(status) {
      const variants = {
        Preparing: 'secondary',
        'To Schedule': 'info',
        Scheduled: 'primary',
        'Confirmed Technician': 'warning',
        Install: 'dark',
        Completed: 'success',
      }

      return variants[status] || 'secondary'
    },

    resetPagination() {
      this.currentPage = 1
    },
    formatDateTime(value) {
      if (!value) return '-'

      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '-'

      return date.toLocaleString()
    },
    openCreateForm() {
      this.formMode = 'create'
      this.selectedWorkOrder = null
      this.showFormModal = true
    },
    openEditForm(item) {
      this.formMode = 'edit'
      this.selectedWorkOrder = { ...item }
      this.showFormModal = true
    },
    async submitForm(payload) {
      this.isSubmitting = true
      try {
        if (this.formMode === 'create') {
          await createWorkOrderPlan(payload)
          this.$toast.success('WO plan created')
        } else {
          await updateWorkOrderPlan(this.selectedWorkOrder.id, payload)
          this.$toast.success('WO plan updated')
        }

        this.showFormModal = false
        await this.fetchWorkOrderPlans()
      } catch (error) {
        this.$toast.error(this.getErrorMessage(error, 'Failed saving WO plan'))
      } finally {
        this.isSubmitting = false
      }
    },
    async confirmDelete(item) {
      const confirmed = window.confirm(`Delete ${item.wo_number}?`)
      if (!confirmed) return

      this.isLoading = true
      try {
        await deleteWorkOrderPlans(item.id)
        this.$toast.success('WO plan deleted')
        await this.fetchWorkOrderPlans()
      } catch (error) {
        this.$toast.error(this.getErrorMessage(error, 'Failed deleting WO plan'))
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" />
    <div class="card mc-card mc-card-front">
      <div class="card-body">
        <div class="d-flex flex-wrap align-items-start justify-content-between">
          <div class="mb-3 mb-md-0">
            <h4 class="mb-2">{{ title }}</h4>
            <p class="text-muted mb-0">{{ description }}</p>
          </div>
          <div>
            <b-button
              v-if="hasAccess(['sms-planner-wo-board:create'])"
              variant="primary"
              class="me-2"
              @click="openCreateForm"
            >
              <i class="bx bx-plus"></i> Create WO Plan
            </b-button>
            <b-button variant="outline-primary" @click="$router.push({ name: 'Planner Menu' })">
              <i class="bx bx-arrow-back"></i> {{ $t('global.back') }}
            </b-button>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="wo-board-filter mb-3">
              <div class="row g-3 align-items-start">
                <div class="col-lg-4 col-md-12">
                  <label class="form-label">Quick Search</label>
                  <div class="d-flex align-items-center">
                    <label class="d-inline-flex align-items-center mc-table-view__search w-100 mb-0">
                      <i class="mc-fa fas fa-search"></i>
                      <b-form-input
                        v-model="filter.search"
                        type="search"
                        placeholder="Search WO number, customer, city, or DT number..."
                        class="ms-2"
                      ></b-form-input>
                    </label>
                    <b-button class="ms-2" variant="outline-secondary" @click="resetFilter">Clear</b-button>
                  </div>
                </div>
                <div class="col-lg-8 col-md-12">
                  <label class="form-label">Quick Filter</label>
                  <div class="d-flex flex-wrap gap-2">
                    <b-button
                      v-for="status in statusFilters"
                      :key="status.value || 'all'"
                      size="sm"
                      :variant="filter.status === status.value ? getStatusVariant(status.value) : 'outline-secondary'"
                      class="wo-board-status-filter"
                      @click="filter.status = status.value"
                    >
                      {{ status.text }}
                    </b-button>
                  </div>
                </div>
              </div>
            </div>

            <div class="table-responsive my-3 border border-1">
              <b-table
                class="datatables mb-0"
                :items="workOrders"
                :fields="fields"
                :current-page="currentPage"
                :per-page="perPage"
                :busy="isLoading"
                show-empty
                responsive="sm"
                hover
                :empty-text="$t('global.emptyTable')"
                :empty-filtered-text="$t('global.emptyFilteredTable')"
              >
                <template #table-busy>
                  <div class="text-center text-primary my-4">
                    <b-spinner class="align-middle me-2" />
                    <strong>Loading...</strong>
                  </div>
                </template>
                <template #cell(wo_number)="data">
                  <strong>
                    <router-link
                      class="text-primary"
                      :to="{ name: 'Planner WO Board Detail', params: { woNumber: data.item.id } }"
                    >
                      {{ data.value }}
                    </router-link>
                  </strong>
                </template>
                <template #cell(plan_datetime)="data">
                  {{ formatDateTime(data.value) }}
                </template>
                <template #cell(estimated_time)="data">
                  {{ formatDateTime(data.value) }}
                </template>
                <template #cell(status)="data">
                  <b-badge :variant="getStatusVariant(data.value)">{{ data.value }}</b-badge>
                </template>
                <template #cell(is_plan_confirmed)="data">
                  <b-badge :variant="data.value ? 'success' : 'secondary'">
                    {{ data.value ? 'Yes' : 'No' }}
                  </b-badge>
                </template>
                <template #cell(actions)="data">
                  <b-button
                    v-if="hasAccess(['sms-planner-wo-board:update'])"
                    size="sm"
                    variant="outline-primary"
                    @click="openEditForm(data.item)"
                  >
                    Edit
                  </b-button>
                  <b-button
                    v-if="hasAccess(['sms-planner-wo-board:delete'])"
                    size="sm"
                    variant="outline-danger"
                    class="ms-2"
                    @click="confirmDelete(data.item)"
                  >
                    Delete
                  </b-button>
                </template>
              </b-table>
            </div>

            <div class="row align-items-center">
              <div class="col-sm-12 col-md-6">
                <div id="tickets-table_length" class="dataTables_length">
                  <label class="d-inline-flex align-items-center mb-0">
                    <span>Menampilkan</span>
                    <span class="mx-2">
                      <b-form-select
                        v-model="perPage"
                        class="form-select form-select-sm"
                        size="sm"
                        :options="pageOptions"
                        @change="resetPagination"
                      />
                    </span>
                    <span>
                      entri. {{ paginationStart }} sampai {{ paginationEnd }} dari {{ totalRows }} entri.
                    </span>
                  </label>
                </div>
              </div>
              <div class="col-sm-12 col-md-6">
                <div class="dataTables_paginate paging_simple_numbers float-end">
                  <ul class="pagination pagination-rounded mb-0">
                    <b-pagination
                      v-model="currentPage"
                      :total-rows="totalRows"
                      :per-page="perPage"
                      class="pagination-rounded mb-0"
                    ></b-pagination>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <WorkOrderPlanForm
      v-model="showFormModal"
      :mode="formMode"
      :item="selectedWorkOrder"
      :loading="isSubmitting"
      :created-by="currentUserId"
      @submit="submitForm"
    />
  </Layout>
</template>

<style scoped>
.wo-board-filter {
  padding: 16px;
  background: #f8f9fa;
  border: 1px solid #eff2f7;
  border-radius: 8px;
}
</style>
